import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSystem } from '@ohif/core';
import PageLayout from '../../custom/components/layouts/PageLayout';
import { authService } from '../../custom/auth/authService';
import { canAccessPage } from '../../custom/utils/permissions';
import { getDefaultRoute } from '../../custom/utils/getDefaultRoute';
import {
  searchConsents,
  getConsentSession,
  getConsentErrorMessage,
  type ConsentSession,
  type ConsentSearchParams,
} from '../../custom/services/abdmConsentService';
import {
  defaultFromDate,
  defaultToDate,
  HI_TYPES_OPTIONS,
} from './abdmConsentConstants';
import RequestConsentDrawer from './RequestConsentDrawer';
import FhirDocumentPanel from './FhirDocumentPanel';
import './abhaConsents.css';

const BREADCRUMBS = [
  { label: 'Dashboard', path: '/' },
  { label: 'RIS', path: '/' },
  { label: 'ABDM Consents' },
];

function statusClass(status?: string): string {
  const s = String(status || '').toUpperCase();
  if (s === 'GRANTED') return 'abha-consent-status--granted';
  if (s === 'REQUESTED') return 'abha-consent-status--requested';
  if (s === 'DENIED' || s === 'FAILED') return 'abha-consent-status--denied';
  if (s === 'REVOKED' || s === 'EXPIRED') return 'abha-consent-status--revoked';
  return 'abha-consent-status--requested';
}

function displayStatus(session: ConsentSession): string {
  const status = String(session.status || session.sessionStatus || 'REQUESTED').toUpperCase();
  if (session.dataEraseAt && new Date(session.dataEraseAt).getTime() < Date.now() && status === 'GRANTED') {
    return 'EXPIRED';
  }
  return status;
}

function patientLabel(session: ConsentSession): string {
  const ids = session.identifiers || {};
  const name = ids.name || '—';
  const abha = ids.abha_address || ids.abhaAddress || ids.abha_number || '—';
  return `${name} / ${abha}`;
}

function facilityLabel(session: ConsentSession): string {
  const artifacts = session.consentArtifacts || [];
  if (!artifacts.length) return '—';
  return artifacts
    .map((a: Record<string, unknown>) => String(a.hipName || a.hipId || ''))
    .filter(Boolean)
    .join(', ') || '—';
}

export default function ABHAConsentsPage() {
  const { servicesManager } = useSystem();
  const uiNotificationService = servicesManager?.services?.uiNotificationService;
  const user = authService.getUser();
  const allowed = canAccessPage(user, '/abha-consents');

  const notify = useCallback(
    (opts: { title: string; message: string; type: 'success' | 'error' | 'info' }) => {
      uiNotificationService?.show({ ...opts, duration: 6000 });
    },
    [uiNotificationService]
  );

  const [filters, setFilters] = useState<ConsentSearchParams>({
    abhaAddress: '',
    drName: '',
    name: '',
    hiTypes: [],
    from: defaultFromDate(),
    to: defaultToDate(),
    page: 1,
  });
  const [sessions, setSessions] = useState<ConsentSession[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState<ConsentSession | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchList = useCallback(
    async (params: ConsentSearchParams) => {
      setLoading(true);
      setError(null);
      try {
        const result = await searchConsents(params);
        setSessions(result.sessions);
        setTotalCount(result.totalCount);
        setPage(result.page);
      } catch (err) {
        setSessions([]);
        setTotalCount(0);
        setError(getConsentErrorMessage(err, 'Failed to load consents'));
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!allowed) return;
    void fetchList(filters);
    // initial load only when allowed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalCount / 10) || 1), [totalCount]);

  if (!allowed) {
    return <Navigate to={getDefaultRoute(user)} replace />;
  }

  const handleSearch = () => {
    const next = { ...filters, page: 1 };
    setFilters(next);
    void fetchList(next);
  };

  const handleClear = () => {
    const next: ConsentSearchParams = {
      abhaAddress: '',
      drName: '',
      name: '',
      hiTypes: [],
      from: defaultFromDate(),
      to: defaultToDate(),
      page: 1,
    };
    setFilters(next);
    void fetchList(next);
  };

  const openSession = async (session: ConsentSession) => {
    const id = String(session._id || '');
    if (!id) {
      setSelected(session);
      return;
    }
    setDetailLoading(true);
    try {
      const detail = await getConsentSession(id);
      setSelected({ ...detail, _id: detail._id || id });
    } catch (err) {
      notify({
        title: 'Unable to load session',
        message: getConsentErrorMessage(err),
        type: 'error',
      });
      setSelected(session);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <PageLayout title="ABDM Consents" breadcrumbs={BREADCRUMBS} headerVariant="light">
      <div className="abha-consents-page">
        <div className="abha-consents-page__header">
          <h1 className="abha-consents-page__title">ABDM Consents</h1>
          <button
            type="button"
            className="abha-consent-btn abha-consent-btn--primary"
            onClick={() => setDrawerOpen(true)}
          >
            Request Consent
          </button>
        </div>

        <div className="abha-consents-toolbar">
          <label className="abha-consent-field">
            <span>ABHA address</span>
            <input
              value={filters.abhaAddress || ''}
              onChange={e => setFilters(f => ({ ...f, abhaAddress: e.target.value }))}
              placeholder="patient@sbx"
              autoComplete="off"
            />
          </label>
          <label className="abha-consent-field">
            <span>Patient name</span>
            <input
              value={filters.name || ''}
              onChange={e => setFilters(f => ({ ...f, name: e.target.value }))}
            />
          </label>
          <label className="abha-consent-field">
            <span>Doctor</span>
            <input
              value={filters.drName || ''}
              onChange={e => setFilters(f => ({ ...f, drName: e.target.value }))}
            />
          </label>
          <label className="abha-consent-field">
            <span>From</span>
            <input
              type="date"
              value={filters.from || ''}
              onChange={e => setFilters(f => ({ ...f, from: e.target.value }))}
            />
          </label>
          <label className="abha-consent-field">
            <span>To</span>
            <input
              type="date"
              value={filters.to || ''}
              onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
            />
          </label>
          <label className="abha-consent-field">
            <span>HI type</span>
            <select
              value={(filters.hiTypes && filters.hiTypes[0]) || ''}
              onChange={e =>
                setFilters(f => ({
                  ...f,
                  hiTypes: e.target.value ? [e.target.value] : [],
                }))
              }
            >
              <option value="">All</option>
              {HI_TYPES_OPTIONS.map(t => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <div className="abha-consents-toolbar__actions">
            <button type="button" className="abha-consent-btn abha-consent-btn--primary" onClick={handleSearch}>
              Search
            </button>
            <button type="button" className="abha-consent-btn abha-consent-btn--ghost" onClick={handleClear}>
              Clear
            </button>
          </div>
        </div>

        <div className="abha-consents-sheet">
          {loading ? <div className="abha-consents-empty">Loading consents…</div> : null}
          {error ? <div className="abha-consents-error">{error}</div> : null}
          {!loading && !error && sessions.length === 0 ? (
            <div className="abha-consents-empty">No consent sessions found for this facility tenant.</div>
          ) : null}
          {!loading && sessions.length > 0 ? (
            <div className="abha-consents-table-wrap">
            <table className="abha-consents-table">
              <thead>
                <tr>
                  <th>Patient / ABHA</th>
                  <th>Source facility</th>
                  <th>Date range</th>
                  <th>Expiry</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {sessions.map(session => {
                  const status = displayStatus(session);
                  return (
                    <tr key={String(session._id)}>
                      <td>{patientLabel(session)}</td>
                      <td>{facilityLabel(session)}</td>
                      <td>
                        {session.fromDate || '—'} → {session.toDate || '—'}
                      </td>
                      <td>{session.dataEraseAt ? new Date(session.dataEraseAt).toLocaleString() : '—'}</td>
                      <td>
                        <span className={`abha-consent-status ${statusClass(status)}`}>{status}</span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="abha-consent-btn abha-consent-btn--ghost"
                          onClick={() => void openSession(session)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          ) : null}
        </div>

        <div className="abha-consents-pagination">
          <button
            type="button"
            className="abha-consent-btn abha-consent-btn--ghost"
            disabled={page <= 1 || loading}
            onClick={() => {
              const next = { ...filters, page: page - 1 };
              setFilters(next);
              void fetchList(next);
            }}
          >
            Previous
          </button>
          <span>
            Page {page} / {totalPages} ({totalCount} total)
          </span>
          <button
            type="button"
            className="abha-consent-btn abha-consent-btn--ghost"
            disabled={page >= totalPages || loading}
            onClick={() => {
              const next = { ...filters, page: page + 1 };
              setFilters(next);
              void fetchList(next);
            }}
          >
            Next
          </button>
        </div>

        {selected ? (
          <div
            className="abha-consent-detail-backdrop"
            role="presentation"
            onClick={() => setSelected(null)}
          >
            <section
              className="abha-consent-detail"
              role="dialog"
              aria-modal="true"
              aria-labelledby="abha-consent-detail-title"
              onClick={event => event.stopPropagation()}
            >
              <header className="abha-consent-detail__header">
                <div>
                  <h2 id="abha-consent-detail-title">
                    {patientLabel(selected)}
                  </h2>
                  <p>
                    {displayStatus(selected)}
                    {detailLoading ? ' · Refreshing…' : ''}
                  </p>
                </div>
                <button
                  type="button"
                  className="abha-consent-btn abha-consent-btn--ghost"
                  onClick={() => setSelected(null)}
                >
                  Close
                </button>
              </header>
              <div className="abha-consent-detail__body">
                <FhirDocumentPanel session={selected} notify={notify} />
              </div>
              <footer className="abha-consent-detail__footer">
                <button
                  type="button"
                  className="abha-consent-btn abha-consent-btn--primary"
                  onClick={() => setSelected(null)}
                >
                  CLOSE
                </button>
              </footer>
            </section>
          </div>
        ) : null}
      </div>

      <RequestConsentDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        notify={notify}
        onRequested={() => {
          void fetchList({ ...filters, page: 1 });
        }}
      />
    </PageLayout>
  );
}
